import { forwardRef } from 'react';
import MuiBox from '@mui/material/Box';
import MuiStack from '@mui/material/Stack';
import FormHelperText from '@mui/material/FormHelperText';

/**
 * The only <form> in the application, rendered through MUI's Box.
 *
 * noValidate is set deliberately. The browser's own bubble validation cannot be
 * styled, is announced inconsistently, and would fire before the yup schema
 * runs — leaving two sources of truth about what is valid. Validation is the
 * schema's job (@srichaitra/validation); this element only submits.
 *
 * The submit handler is wrapped so preventDefault is never forgotten, which is
 * the mistake that reloads the page and loses everything the user typed.
 */
export const Form = forwardRef(function Form({ onSubmit, children, ...rest }, ref) {
  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit?.(event);
  };

  return (
    <MuiBox
      ref={ref}
      component="form"
      noValidate
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}
      {...rest}
    >
      {children}
    </MuiBox>
  );
});

/**
 * A group of related controls.
 *
 * `legend` is required rather than optional: a fieldset with no legend gives a
 * screen reader a group boundary with no name, which is worse than no grouping
 * at all.
 */
export function Fieldset({ legend, children, ...rest }) {
  return (
    <MuiBox
      component="fieldset"
      sx={{ m: 0, p: 4, border: 1, borderColor: 'divider', borderRadius: 1 }}
      {...rest}
    >
      <MuiBox component="legend" sx={{ px: 1, typography: 'body2', color: 'text.secondary' }}>
        {legend}
      </MuiBox>
      {children}
    </MuiBox>
  );
}

/** The row of buttons that closes a form. */
export function FormActions({ children, sx, ...rest }) {
  return (
    <MuiStack
      direction="row"
      spacing={2}
      useFlexGap
      // Layout goes through sx: Stack does not forward the system props.
      sx={{ flexWrap: 'wrap', alignItems: 'center', ...sx }}
      {...rest}
    >
      {children}
    </MuiStack>
  );
}

/**
 * A field's error text, for controls that do not render their own.
 *
 * The TextField-based controls in this library already show their error through
 * MUI's helperText; this is for anything assembled by hand. Rendered only when
 * there is something to say — an empty live region that appears and disappears
 * gets announced as a change.
 */
export function FieldError({ id, children, ...rest }) {
  if (!children) return null;

  return (
    <FormHelperText id={id} error role="alert" {...rest}>
      {children}
    </FormHelperText>
  );
}

/** Static helper text, referenced by a control's aria-describedby. */
export function FieldHint({ id, children, ...rest }) {
  if (!children) return null;

  return (
    <FormHelperText id={id} {...rest}>
      {children}
    </FormHelperText>
  );
}
